import dayjs from 'dayjs';
import { IAppointment } from './IAppointment';
import { ISO8601 } from '../support/ISO8601';

export type IGroupedAppointments = {[key: string]: IAppointment[]};

export class AppointmentGrouper {
    private appointments: IAppointment[];
    private minDate?: ISO8601;
    private showEmptyToday: boolean;

    constructor(appointments: IAppointment[], minDate?: ISO8601, showEmptyToday: boolean = false) {
        this.appointments = appointments;
        this.minDate = minDate;
        this.showEmptyToday = showEmptyToday;
    }

    private isOnOrAfterDate(date: ISO8601, onOrAfterDate: ISO8601): boolean {
        const after = dayjs(onOrAfterDate).subtract(1, 'days');
        return dayjs(date).isAfter(after, 'day');
    }

    private times(x: number) {
        return (f: (y: number) => void) => {
            if (x > 0) {
                f(x);
                this.times(x-1)(f);
            }
        };
    }

    private sortObject(o: IGroupedAppointments): IGroupedAppointments {
        return Object.keys(o).sort().reduce((r: any, k) => {
            r[k] = o[k];
            return r;
        }, {});
    }

    public group(): IGroupedAppointments {
        let filteredAppointments = this.appointments;

        // only show minDate and forward
        if (this.minDate) {
            filteredAppointments = filteredAppointments.filter(appt =>
                this.isOnOrAfterDate(appt.end, this.minDate!)
            );
        }

        // group dates by day
        const groupedAppointments: IGroupedAppointments = filteredAppointments.reduce((groups: IGroupedAppointments, appt) => {
            const sDate = dayjs(appt.start).format('YYYY-MM-DD');
            const diffInDays = dayjs(appt.end).diff(dayjs(appt.start), 'days');

            // if the end date is before minDate, don't add it
            if (!this.minDate || this.isOnOrAfterDate(appt.start, this.minDate)) {
                // if there's no start date collection, create it
                if (!groups[sDate]) {
                    groups[sDate] = [];
                }
                groups[sDate].push(appt);
            }

            // loop over the number of days it spans, make sure it's in each days group
            this.times(diffInDays)((y) => {
                const d = dayjs(sDate).add(y, 'days').format('YYYY-MM-DD');
                // if the group date is before minDate, don't add it
                if (!this.minDate || this.isOnOrAfterDate(d, this.minDate)) {
                    if (!groups[d]) {
                        groups[d] = [];
                    }
                    groups[d].push(appt);
                }
            });

            return groups;
        }, {});


        const todayStr = dayjs().format('YYYY-MM-DD');
        const hasApptsToday = todayStr in groupedAppointments;

        // if no today, create one and sort
        if (this.showEmptyToday && !hasApptsToday) {
            groupedAppointments[todayStr] = [];
            return this.sortObject(groupedAppointments);
        }

        return groupedAppointments;
    }
};
